import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AuthService } from "@app/auth.service";
import { ReadOnlyTokenStorage } from "@app/read-only-token.storage";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { TemplateLocaleService } from "@domain/project/template";
import { SessionUser } from "@domain/user/session-user";
import { User } from "@domain/user/user";
import { environment } from "@environments/environment";
import { BusinessScope } from "@lib/constants/business-scope";
import { mapRightsToAuthRole } from "@lib/constants/rights-role-mapping";
import { UpdateUserRequestDto } from "@lib/dtos/user/update-user-request.dto";
import { UserDto } from "@lib/dtos/user/user.dto";
import { ObservableInstanceMapper } from "@utils/observable-instance-mapper";
import { catchError, filter, first, forkJoin, fromEvent, map, Observable, of, ReplaySubject, switchMap, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class UserService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private templateLocaleService = inject(TemplateLocaleService);
  private projectApiService = inject(ProjectApiService);
  private readOnlyTokenStorage = inject(ReadOnlyTokenStorage);

  public static readonly USER_URL: string = environment.apiUrl + "/user";
  public static readonly PENDING_ZREF_STORAGE_KEY = "navinta.pendingZrefPrices";
  private readonly _user$ = new ReplaySubject<User>(1);
  private readonly COUNTRY_KEY_ACCOUNT_MANAGER: string = "countryKeyAccountManager";
  private readonly GLOBAL_ADMIN: string = "globalAdmin";
  private LANGUAGE_LOCALE_MAP: Record<string, string> = {
    en: "en-US",
    de: "de-DE",
  };

  constructor() {
    this.initUser().subscribe({
      next: (user) => {
        const pendingZref = this.getPendingZrefChange();
        if (pendingZref !== null) {
          user.showZrefPrices = pendingZref;
        }
        this._user$.next(user);
      },
      error: (err) => this._user$.error(err),
    });

    fromEvent(window, "online").subscribe(() => {
      this.syncPendingZrefChange();
    });
  }

  get user$(): Observable<User> {
    return this._user$.asObservable();
  }

  get businessScope$(): Observable<BusinessScope> {
    return this._user$.asObservable().pipe(
      map((user) => user.scope),
      filter((scope): scope is BusinessScope => !!scope),
    );
  }

  updateLanguage(newLanguage: string): Observable<User> {
    return this.updateUserViaApi({ language: newLanguage }).pipe(
      tap((user) => {
        this._user$.next(user);
      }),
    );
  }

  updateCountry(newCountry: string): Observable<User> {
    return this.updateUserViaApi({ country: newCountry }).pipe(
      tap((user: User) => {
        this._user$.next(user);
      }),
    );
  }

  isGlobalAdmin(): Observable<boolean> {
    return this.user$.pipe(map((user) => user.role["user"] === this.GLOBAL_ADMIN));
  }

  getCountryKeys(): Observable<string[]> {
    return this.user$.pipe(
      switchMap((user) => {
        return this.checkForCountryKeyAccountManager(user.role);
      }),
    );
  }

  updateUser(scope: string, user: User) {
    const updatedUser: UpdateUserRequestDto = {
      country: user.country,
      language: user.language,
      businessScope: scope,
    };

    return this.updateUserViaApi(updatedUser).pipe(
      tap((user: User) => {
        this._user$.next(user);
      }),
    );
  }

  toggleShowZrefPrices(): Observable<User> {
    return this._user$.pipe(
      first(),
      switchMap((user) => {
        const newValue = !user.showZrefPrices;
        return this.updateUserViaApi({ showZrefPrices: newValue }).pipe(
          tap((updatedUser) => {
            updatedUser.showZrefPrices = newValue;
            this._user$.next(updatedUser);
          }),
        );
      }),
    );
  }

  updateShowZrefPrices(value: boolean): Observable<User> {
    return this.updateUserViaApi({ showZrefPrices: value }).pipe(
      tap((updatedUser) => {
        updatedUser.showZrefPrices = value;
        this._user$.next(updatedUser);
      }),
    );
  }

  applyZrefPricesLocally(value: boolean): void {
    this._user$.pipe(first()).subscribe((user) => {
      user.showZrefPrices = value;
      this._user$.next(user);
      localStorage.setItem(UserService.PENDING_ZREF_STORAGE_KEY, JSON.stringify(value));
    });
  }

  getPendingZrefChange(): boolean | null {
    const stored = localStorage.getItem(UserService.PENDING_ZREF_STORAGE_KEY);
    if (stored === null) return null;
    try {
      return JSON.parse(stored) as boolean;
    } catch {
      return null;
    }
  }

  syncPendingZrefChange(): void {
    const pending = this.getPendingZrefChange();
    if (pending === null) return;
    this.updateShowZrefPrices(pending).subscribe({
      next: () => localStorage.removeItem(UserService.PENDING_ZREF_STORAGE_KEY),
    });
  }

  private checkForCountryKeyAccountManager(role: any): Observable<string[]> {
    const keys = Object.keys(role).filter((key) => role[key] === this.COUNTRY_KEY_ACCOUNT_MANAGER);
    if (keys.length) {
      return of(this.extractCountryKeys(keys));
    }
    return of([]);
  }

  private extractCountryKeys(keys: string[]): string[] {
    return keys.map((key) => key.replace("user/", ""));
  }

  private initUser(): Observable<User> {
    return this.authService.userInfo$.pipe(
      switchMap((userInfo: SessionUser) => {
        if (!userInfo.id) {
          return this.initReadAccessUser();
        } else {
          return this.loadUserFromApi().pipe(
            switchMap((user: User) => {
              const languages$ = this.templateLocaleService?.allSupportedLanguageCodes$ ?? of([] as string[]);
              const countries$ = this.templateLocaleService?.allSupportedCountryCodes$ ?? of([] as string[]);
              return forkJoin({ languages: languages$, countries: countries$ }).pipe(
                switchMap(({ languages, countries }) => {
                  const isValidLanguage = this.isValidLanguage(user, languages);
                  const isValidCountry = this.isValidCountry(user, countries);
                  if (!isValidLanguage || !isValidCountry) {
                    const updateData: Record<string, string> = {
                      language: isValidLanguage ? user.language : this.getDefaultLanguage(languages),
                      country: isValidCountry ? user.country : countries[0],
                    };
                    return this.updateUserViaApi(updateData);
                  }
                  return of(user);
                }),
              );
            }),
          );
        }
      }),
    );
  }

  private getDefaultLanguage(supportedLanguages: string[]): string {
    const browserLanguage = this.getBrowserLanguage();
    return supportedLanguages.includes(browserLanguage) ? browserLanguage : supportedLanguages[0];
  }

  private getBrowserLanguage(): string {
    const browserLanguage = navigator.language;

    if (browserLanguage.length === 2) {
      return this.LANGUAGE_LOCALE_MAP[browserLanguage] ?? browserLanguage;
    }

    return browserLanguage;
  }

  private initReadAccessUser(): Observable<User> {
    const languages$ = this.templateLocaleService?.allSupportedLanguageCodes$ ?? of([] as string[]);
    return this.resolveReadOnlyBusinessScope().pipe(
      switchMap((scope) => languages$.pipe(map((supportedLanguages) => this.buildReadOnlyUser(supportedLanguages, scope)))),
    );
  }

  private resolveReadOnlyBusinessScope(): Observable<BusinessScope> {
    const readAccessToken = this.readOnlyTokenStorage.get();
    const storedScope = this.toBusinessScope(this.readOnlyTokenStorage.getBusinessScope(readAccessToken ?? ""));
    const projectId = this.getReadAccessProjectId(readAccessToken);

    if (!projectId || storedScope) {
      return of(storedScope ?? BusinessScope.FGDS);
    }

    return this.projectApiService.getBusinessScopeByProjectId(projectId).pipe(
      tap((scope) => {
        if (readAccessToken) {
          this.readOnlyTokenStorage.setBusinessScope(scope, readAccessToken);
        }
      }),
      catchError(() => of(storedScope ?? BusinessScope.FGDS)),
    );
  }

  private getReadAccessProjectIdFromUrl(): string | null {
    if (!window.location.hash.includes("?")) {
      return null;
    }

    const queryPart = window.location.hash.substring(window.location.hash.indexOf("?"));
    return new URLSearchParams(queryPart).get("projectId");
  }

  private getReadAccessProjectId(readAccessToken: string | null): string | null {
    return (
      this.getReadAccessProjectIdFromUrl() ?? (readAccessToken ? this.readOnlyTokenStorage.getProjectId(readAccessToken) : null)
    );
  }

  private toBusinessScope(scope?: string | null): BusinessScope | undefined {
    if (!scope) {
      return undefined;
    }

    if (scope === BusinessScope.FGDS || scope === BusinessScope.MONITORING) {
      return scope;
    }

    return undefined;
  }

  private buildReadOnlyUser(supportedLanguages: string[], scope: BusinessScope): User {
    const user = new User();
    const storedLanguage = this.readOnlyTokenStorage.getLanguage();
    const fallbackLanguage = "en-US";
    user.language = storedLanguage && supportedLanguages.includes(storedLanguage) ? storedLanguage : fallbackLanguage;
    user.readOnlyAccess = true;
    user.scope = scope;
    return user;
  }

  private loadUserFromApi(): Observable<User> {
    return ObservableInstanceMapper.valueToInstance(this.http.get<UserDto>(UserService.USER_URL), UserDto).pipe(
      switchMap((apiUser) => this.buildUser(apiUser)),
    );
  }

  private updateUserViaApi(updatedData: UpdateUserRequestDto): Observable<User> {
    return ObservableInstanceMapper.valueToInstance(this.http.patch<UserDto>(UserService.USER_URL, updatedData), UserDto).pipe(
      switchMap((apiUser) => this.buildUser(apiUser)),
    );
  }

  private isValidLanguage(user: User, supportedLanguages: string[]) {
    return supportedLanguages.includes(user.language);
  }

  private isValidCountry(user: User, supportedCountries: string[]) {
    return supportedCountries.includes(user.country);
  }

  private buildUser(apiUser: UserDto): Observable<User> {
    return this.authService.userInfo$.pipe(
      first(),
      map((userInfo: SessionUser) => {
        const user = new User();
        user.id = apiUser.id ?? "";
        user.country = apiUser.country ?? "";
        user.language = apiUser.language ?? "";
        user.productFruitsHmac = apiUser.productFruitsHmac ?? "";
        user.firstName = userInfo.firstName!;
        user.lastName = userInfo.lastName!;
        user.email = userInfo.email!;
        user.role = userInfo.rights ? mapRightsToAuthRole(userInfo.rights) : userInfo.authLevel!;
        user.readOnlyAccess = false;
        user.scope = (userInfo.scope as BusinessScope) ?? undefined;
        user.showZrefPrices = apiUser.showZrefPrices ?? false;
        return user;
      }),
    );
  }
}
